import React, { useState, useEffect, useMemo } from "react";
import { 
  CheckCircle2, Circle, Check, Wallet, 
  ChevronRight, SearchX, Home, ChevronRight as ChevronRightIcon,
  SlidersHorizontal, PartyPopper, CalendarDays, Layers, Tag, ArrowDownWideNarrow
} from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const shortId = (id) => id ? id.split('-').slice(0, 2).join('-') : '—';

/* ─────────────────────────────────────────────
   SKELETON LOADER (Descriptive Ticket)
───────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex gap-4 animate-pulse">
    <div className="w-5 h-5 rounded-full bg-slate-200 dark:bg-slate-700 shrink-0 mt-1" />
    <div className="flex-1 space-y-3 py-1">
      <div className="flex gap-2"><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3" /><div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4" /></div>
      <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
      <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
    </div>
    <div className="w-20 h-full border-l border-dashed border-slate-200 dark:border-slate-700 pl-4 py-2 flex flex-col justify-center">
      <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-full mb-2" />
      <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-full" />
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   SUCCESS MODAL COMPONENT
───────────────────────────────────────────── */
const SuccessModal = ({ isOpen, onClose, count, amount }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-sm p-8 text-center animate-in zoom-in-95 slide-in-from-bottom-4 duration-300">
        <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-5 shadow-inner">
          <PartyPopper size={36} className="text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2">Redeemed!</h3>
        <p className="text-slate-500 dark:text-slate-400 font-medium mb-6">
          Successfully processed <strong className="text-slate-700 dark:text-slate-200">{count} coupons</strong> for a total of <strong className="text-emerald-600 dark:text-emerald-400">{fmtINR(amount)}</strong>.
        </p>
        <button 
          onClick={onClose}
          className="w-full bg-slate-900 dark:bg-indigo-600 hover:bg-slate-800 dark:hover:bg-indigo-700 text-white font-bold py-3.5 rounded-xl transition-all active:scale-[0.98]"
        >
          Awesome, Thanks!
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const MyCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // ─── FILTER & SORT STATE ───
  const [filters, setFilters] = useState({
    category: 'all',
    stage: 'all',
    dateRange: 'all',
    sort: 'newest'
  });
  
  const [modalState, setModalState] = useState({ isOpen: false, count: 0, amount: 0 });

  // ─── API FETCH ───
  useEffect(() => {
    const fetchCoupons = async () => {
      try {
        const res = await axiosInstance.get('/commission/coupons');
        if (res.data && res.data.data) setCoupons(res.data.data);
      } catch (err) {
        console.error("Failed to fetch coupons:", err);
        // Fallback exact mock data
        setCoupons([
          { id: "COM-1776238734001-2-4L22", payment_id: "PAYU39B-1776238713427", payer_id: "RKXHTISLOF", payer_name: "Drogon", depth: 2, installment: 1, category: "joining", amount: "100.00", stage: "earned", created_at: "2026-04-15T07:38:54.000Z" },
          { id: "COM-1776238389678-1-L3ZF", payment_id: "PAYL655-1776238367617", payer_id: "RKAIP15D0W", payer_name: "Daenerys Targaryen", depth: 1, installment: 1, category: "joining", amount: "500.00", stage: "earned", created_at: "2026-04-15T07:33:09.000Z" },
          { id: "COM-1776238389679-3-XXXX", payment_id: "PAYL655-1776238367618", payer_id: "RKAIP15D0X", payer_name: "Jon Snow", depth: 3, installment: 4, category: "installment", amount: "250.00", stage: "pending", created_at: "2026-04-10T14:20:00.000Z" },
          { id: "COM-1776238389680-1-YYYY", payment_id: "PAYL655-1776238367619", payer_id: "RKAIP15D0Y", payer_name: "Arya Stark", depth: 1, installment: 2, category: "installment", amount: "500.00", stage: "earned", created_at: "2026-03-25T09:15:00.000Z" },
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCoupons();
  }, []);

  // ─── FILTER & SORT LOGIC ───
  const displayedCoupons = useMemo(() => {
    let result = coupons;
    
    if (filters.category !== 'all') result = result.filter(c => c.category === filters.category);
    if (filters.stage !== 'all') result = result.filter(c => c.stage === filters.stage);
    if (filters.dateRange !== 'all') {
      const now = new Date();
      const daysAgo = new Date();
      daysAgo.setDate(now.getDate() - parseInt(filters.dateRange));
      result = result.filter(c => new Date(c.created_at) >= daysAgo);
    }

    return result.sort((a, b) => {
      const amtA = parseFloat(a.amount); const amtB = parseFloat(b.amount);
      const dateA = new Date(a.created_at); const dateB = new Date(b.created_at);
      
      if (filters.sort === 'newest') return dateB - dateA;
      if (filters.sort === 'oldest') return dateA - dateB;
      if (filters.sort === 'highest') return amtB - amtA;
      if (filters.sort === 'lowest') return amtA - amtB;
      return 0;
    });
  }, [coupons, filters]);

  // ─── SELECTION LOGIC ───
  const toggleSelection = (id) => {
    const newSelected = new Set(selectedIds);
    newSelected.has(id) ? newSelected.delete(id) : newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === displayedCoupons.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(displayedCoupons.map(c => c.id)));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSelectedIds(new Set()); // Reset selection on filter change
  };

  const handleRedeem = () => {
    const selectedArray = coupons.filter(c => selectedIds.has(c.id));
    const totalAmt = selectedArray.reduce((sum, c) => sum + parseFloat(c.amount), 0);
    setModalState({ isOpen: true, count: selectedIds.size, amount: totalAmt });
    setCoupons(prev => prev.filter(c => !selectedIds.has(c.id)));
    setSelectedIds(new Set());
  };

  const totalSelectedAmount = coupons.filter(c => selectedIds.has(c.id)).reduce((sum, c) => sum + parseFloat(c.amount), 0);
  const isAllSelected = displayedCoupons.length > 0 && selectedIds.size === displayedCoupons.length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pb-32 transition-colors duration-200">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        
        {/* ── BREADCRUMBS ── */}
        <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 mb-4">
          <Home size={14} />
          <ChevronRightIcon size={12} className="opacity-50" />
          <span>Commission</span>
          <ChevronRightIcon size={12} className="opacity-50" />
          <span className="text-indigo-600 dark:text-indigo-400">My Coupons</span>
        </div>

        {/* ── HEADER ── */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">My Coupons</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium mt-1">
              Manage and redeem your network commissions
            </p>
          </div>
          
          {displayedCoupons.length > 0 && (
            <button 
              onClick={handleSelectAll}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold border transition-all duration-200 shadow-sm ${
                isAllSelected 
                  ? "bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:border-indigo-500" 
                  : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
              }`}
            >
              {isAllSelected ? <CheckCircle2 size={16} /> : <Circle size={16} className="text-slate-400 dark:text-slate-500" />}
              {isAllSelected ? "Deselect All" : "Select All"}
            </button>
          )}
        </div>

        {/* ── ADVANCED FILTER TOOLBAR ── */}
        <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm mb-6 transition-colors duration-200">
          <div className="flex items-center gap-2 px-2 border-r border-slate-200 dark:border-slate-700">
            <SlidersHorizontal size={16} className="text-slate-400 dark:text-slate-500"/>
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider hidden sm:block pr-2">Filters</span>
          </div>

          <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} className="bg-transparent text-sm font-semibold text-slate-700 dark:text-slate-200 outline-none cursor-pointer py-1">
            <option value="all">All Categories</option>
            <option value="joining">Joining Fees</option>
            <option value="installment">Installments</option>
          </select>

          <select value={filters.stage} onChange={(e) => handleFilterChange('stage', e.target.value)} className="bg-transparent text-sm font-semibold text-slate-700 dark:text-slate-200 outline-none cursor-pointer py-1">
            <option value="all">All Stages</option>
            <option value="earned">Earned</option>
            <option value="pending">Pending</option>
          </select>

          <select value={filters.dateRange} onChange={(e) => handleFilterChange('dateRange', e.target.value)} className="bg-transparent text-sm font-semibold text-slate-700 dark:text-slate-200 outline-none cursor-pointer py-1">
            <option value="all">All Time</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
          </select>

          <div className="flex-1" /> {/* Spacer */}

          <div className="flex items-center gap-2 pl-3 border-l border-slate-200 dark:border-slate-700">
            <ArrowDownWideNarrow size={16} className="text-slate-400 dark:text-slate-500"/>
            <select value={filters.sort} onChange={(e) => handleFilterChange('sort', e.target.value)} className="bg-transparent text-sm font-semibold text-slate-700 dark:text-slate-200 outline-none cursor-pointer py-1">
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Amount</option>
              <option value="lowest">Lowest Amount</option>
            </select>
          </div>
        </div>

        {/* ── COUPON GRID ── */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <SkeletonCard key={i} />)}
          </div>
        ) : displayedCoupons.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-16 flex flex-col items-center text-center shadow-sm">
            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-700/50 rounded-full flex items-center justify-center mb-4"><SearchX size={32} className="text-slate-400 dark:text-slate-500" /></div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">No coupons found</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Try adjusting your filters or wait for new commissions.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {displayedCoupons.map((item) => {
              const isSelected = selectedIds.has(item.id);
              const isJoining = item.category === 'joining';
              const isEarned = item.stage === 'earned';

              return (
                <div 
                  key={item.id}
                  onClick={() => toggleSelection(item.id)}
                  className={`relative flex items-stretch cursor-pointer rounded-xl transition-all duration-200 border bg-white dark:bg-slate-800 ${
                    isSelected 
                      ? "border-indigo-500 dark:border-indigo-400 shadow-md shadow-indigo-500/15 ring-1 ring-indigo-500 dark:bg-indigo-900/20" 
                      : "border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-500 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-black/50"
                  }`}
                >
                  {/* Color Accent Edge */}
                  <div className={`absolute left-0 top-0 bottom-0 w-1.5 rounded-l-xl transition-colors ${
                    isSelected ? "bg-indigo-500 dark:bg-indigo-400" : isJoining ? "bg-amber-400 dark:bg-amber-500" : "bg-blue-400 dark:bg-blue-500"
                  }`} />

                  {/* ── Left: Info Section ── */}
                  <div className="flex-1 p-4 pl-6 pr-4 flex items-start gap-3">
                    
                    {/* Checkbox */}
                    <div className={`w-5 h-5 mt-1 shrink-0 rounded-full border-[1.5px] flex items-center justify-center transition-colors ${
                      isSelected ? "bg-indigo-600 border-indigo-600 dark:bg-indigo-500 dark:border-indigo-500" : "border-slate-300 dark:border-slate-600"
                    }`}>
                      {isSelected && <Check size={12} className="text-white" strokeWidth={3} />}
                    </div>

                    <div className="flex-1 min-w-0 space-y-2">
                      
                      {/* Top Badges */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          isJoining ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' : 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400'
                        }`}>
                          <Tag size={10}/> {isJoining ? 'Joining' : `Inst #${item.installment}`}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          isEarned ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {item.stage}
                        </span>
                      </div>

                      {/* Main Details */}
                      <div>
                        <div className="flex items-center justify-between mb-0.5">
                          <h3 className="text-[15px] font-bold text-slate-900 dark:text-white truncate pr-2" title={item.payer_name}>
                            {item.payer_name}
                          </h3>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-y-1 mt-2 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                          <span className="flex items-center gap-1.5"><Layers size={12}/> Lvl {item.depth}</span>
                          <span className="flex items-center gap-1.5"><CalendarDays size={12}/> {fmtDate(item.created_at)}</span>
                          <span className="col-span-2 text-[10px] font-mono opacity-60 mt-1">ID: {shortId(item.id)}</span>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* ── Right: Tear-off Amount Section ── */}
                  <div className="relative flex flex-col justify-center items-center px-5 py-4 border-l border-dashed border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/50 rounded-r-xl min-w-[100px]">
                    
                    {/* Cutouts (Matches parent background `bg-slate-50 dark:bg-slate-900`) */}
                    <div className="absolute -left-2.5 top-[-10px] w-5 h-5 rounded-full bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700" />
                    <div className="absolute -left-2.5 bottom-[-10px] w-5 h-5 rounded-full bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700" />
                    
                    <span className="text-[10px] font-bold text-slate-400 dark:text-slate-500 mb-1 uppercase tracking-wider">Value</span>
                    <div className="flex items-start text-slate-900 dark:text-white">
                      <span className="text-xs font-bold mt-1 mr-0.5 text-indigo-600 dark:text-indigo-400">₹</span>
                      <span className="text-2xl font-black tracking-tight">{parseFloat(item.amount)}</span>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── FLOATING REDEEM ACTION BAR ── */}
      <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-40 transition-all duration-400 ease-out ${
        selectedIds.size > 0 ? "translate-y-0 opacity-100 scale-100" : "translate-y-12 opacity-0 scale-95 pointer-events-none"
      }`}>
        <div className="bg-slate-900 dark:bg-slate-800 text-white rounded-2xl shadow-2xl p-3 pr-4 flex items-center gap-6 border border-slate-700/50 dark:border-slate-600 backdrop-blur-lg min-w-[320px]">
          
          <div className="flex items-center gap-4 pl-3">
            <div className="w-10 h-10 rounded-full bg-indigo-500/20 flex items-center justify-center">
              <Wallet size={20} className="text-indigo-400" />
            </div>
            <div>
              <div className="text-slate-300 text-xs font-bold mb-0.5">
                {selectedIds.size} {selectedIds.size === 1 ? 'coupon' : 'coupons'} selected
              </div>
              <div className="text-lg font-black tracking-tight flex items-baseline gap-1">
                <span className="text-emerald-400 text-sm">Total:</span> 
                {fmtINR(totalSelectedAmount)}
              </div>
            </div>
          </div>

          <button 
            onClick={handleRedeem}
            className="ml-auto bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2.5 px-6 rounded-xl flex items-center gap-2 transition-all active:scale-95 shadow-lg shadow-indigo-900/50"
          >
            <span>Redeem</span>
            <ChevronRight size={16} strokeWidth={3} />
          </button>
        </div>
      </div>

      {/* ── SUCCESS MODAL ── */}
      <SuccessModal 
        isOpen={modalState.isOpen} 
        onClose={() => setModalState({ isOpen: false, count: 0, amount: 0 })}
        count={modalState.count}
        amount={modalState.amount}
      />

    </div>
  );
};

export default MyCoupons;